import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Bell, BookOpen, FileText, User, LogOut, Home, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import WriterOverview from './writer/WriterDashboard';
import SubmitBook from './writer/SubmitBook';
import MySubmissions from './writer/MySubmissions';
import WriterProfile from './writer/WriterProfile';
import MyEarnings from './shared/MyEarnings';
import NotificationPanel from '../components/NotificationPanel';

const WriterDashboard = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000); // Poll every 30s
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const navItems = [
    { path: '/writer', icon: Home, label: 'Oorsig' },
    { path: '/writer/submit', icon: BookOpen, label: 'Dien Boek In' },
    { path: '/writer/submissions', icon: FileText, label: 'My Indienings' },
    { path: '/writer/earnings', icon: TrendingUp, label: 'My Verdienste' },
    { path: '/writer/profile', icon: User, label: 'Profiel' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100">
      {/* Header */}
      <header className="bg-white border-b-2 border-orange-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div>
              <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                DIE BIBLIOTEEK
              </h1>
              <p className="text-sm text-gray-600">Skrywer Dashboard</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="relative">
                <Button
                  data-testid="notifications-btn"
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative"
                >
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-orange-500">
                      {unreadCount}
                    </Badge>
                  )}
                </Button>
                {showNotifications && (
                  <NotificationPanel
                    notifications={notifications}
                    onClose={() => setShowNotifications(false)}
                    onRefresh={fetchNotifications}
                  />
                )}
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-gray-900">{user?.voornaam} {user?.van}</p>
                <p className="text-xs text-gray-500">Skrywer</p>
              </div>
              <Button
                data-testid="logout-btn"
                variant="outline"
                size="sm"
                onClick={logout}
                className="flex items-center gap-2"
              >
                <LogOut className="h-4 w-4" />
                Meld Af
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Sidebar */}
          <aside className="w-full md:w-64 space-y-2">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link key={item.path} to={item.path}>
                  <Button
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    variant={isActive ? 'default' : 'ghost'}
                    className={`w-full justify-start gap-3 ${
                      isActive
                        ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700'
                        : 'hover:bg-orange-100'
                    }`}
                  >
                    <item.icon className="h-5 w-5" />
                    {item.label}
                  </Button>
                </Link>
              );
            })}
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            <Routes>
              <Route index element={<WriterOverview />} />
              <Route path="submit" element={<SubmitBook />} />
              <Route path="submissions" element={<MySubmissions />} />
              <Route path="earnings" element={<MyEarnings />} />
              <Route path="profile" element={<WriterProfile />} />
            </Routes>
          </main>
        </div>
      </div>
    </div>
  );
};

export default WriterDashboard;